import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { useDocuments } from '@/hooks/useDocuments'
import { extractionService } from '@/services/extraction.service'
import type { Document, SubmissionWithDetails } from '@/types'
import { UploadDropzone } from '@/components/upload/UploadDropzone'
import { DocumentList } from '@/components/upload/DocumentList'
import { DocumentViewer } from './DocumentViewer'
import { RiskProfilePanel } from './RiskProfilePanel'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { ResizableHandle, ResizablePanel, ResizablePanelGroup } from '@/components/ui/resizable'
import { ArrowLeft, Play, Loader2 } from 'lucide-react'
import { toast } from 'sonner'

interface WorkspaceLayoutProps {
  submission: SubmissionWithDetails
  onStatusChange?: () => void
}

export function WorkspaceLayout({ submission, onStatusChange }: WorkspaceLayoutProps) {
  const queryClient = useQueryClient()
  const [selectedDocument, setSelectedDocument] = useState<Document | null>(null)
  const [processingDocs, setProcessingDocs] = useState<Set<string>>(new Set())

  const { data: documents, refetch: refetchDocuments } = useDocuments(submission.id)

  // Auto-select first document
  useEffect(() => {
    if (documents && documents.length > 0 && !selectedDocument) {
      setSelectedDocument(documents[0])
    }
  }, [documents, selectedDocument])

  const handleProcessDocument = async (doc: Document) => {
    if (processingDocs.has(doc.id)) return

    // Check if API key is configured
    const config = extractionService.getConfig()
    if (!config.hasApiKey) {
      toast.error('Nanonets API key not configured. Go to Settings → API Keys')
      return
    }

    setProcessingDocs((prev) => new Set(prev).add(doc.id))
    toast.info('Processing document with Nanonets...')

    try {
      const result = await extractionService.processDocument(doc.id, submission.id)
      
      if (result.success) {
        toast.success(`Extracted ${result.fieldsExtracted} fields`)
        queryClient.invalidateQueries({ queryKey: ['documents', submission.id] })
        queryClient.invalidateQueries({ queryKey: ['extracted-fields', submission.id] })
        refetchDocuments()
      } else {
        toast.error(`Processing failed: ${result.error}`)
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to process'
      toast.error(errorMessage)
      console.error('Processing error:', error)
    } finally {
      setProcessingDocs((prev) => {
        const next = new Set(prev)
        next.delete(doc.id)
        return next
      })
    }
  }

  const handleProcessAllPending = async () => {
    const pendingDocs = documents?.filter((d) => d.status === 'pending') || []
    for (const doc of pendingDocs) {
      await handleProcessDocument(doc)
    }
  }

  const pendingCount = documents?.filter((d) => d.status === 'pending').length || 0

  return (
    <div className="flex h-screen flex-col">
      {/* Header */}
      <div className="flex items-center justify-between border-b p-4">
        <div className="flex items-center gap-4">
          <Button asChild variant="ghost" size="icon">
            <Link to="/dashboard">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1 className="text-xl font-semibold">{submission.insured_name}</h1>
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="capitalize">
                {submission.status}
              </Badge>
              {submission.risk_score !== null && (
                <span className="text-sm text-muted-foreground">
                  Risk Score: {submission.risk_score}%
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {pendingCount > 0 && (
            <Button onClick={handleProcessAllPending} disabled={processingDocs.size > 0}>
              {processingDocs.size > 0 ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Play className="mr-2 h-4 w-4" />
                  Process All ({pendingCount})
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Main Content */}
      <ResizablePanelGroup direction="horizontal" className="flex-1">
        {/* Left Panel - Documents */}
        <ResizablePanel defaultSize={25} minSize={20} maxSize={35}>
          <Tabs defaultValue="documents" className="flex h-full flex-col">
            <div className="border-b px-4 pt-2">
              <TabsList className="w-full">
                <TabsTrigger value="documents" className="flex-1">
                  Documents
                </TabsTrigger>
                <TabsTrigger value="upload" className="flex-1">
                  Upload
                </TabsTrigger>
              </TabsList>
            </div>
            <TabsContent value="documents" className="flex-1 overflow-auto p-4 mt-0">
              <DocumentList
                submissionId={submission.id}
                selectedDocumentId={selectedDocument?.id}
                onDocumentSelect={setSelectedDocument}
              />
              {selectedDocument && selectedDocument.status === 'pending' && (
                <Button
                  className="mt-4 w-full"
                  onClick={() => handleProcessDocument(selectedDocument)}
                  disabled={processingDocs.has(selectedDocument.id)}
                >
                  {processingDocs.has(selectedDocument.id) ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <Play className="mr-2 h-4 w-4" />
                      Process Document
                    </>
                  )}
                </Button>
              )}
            </TabsContent>
            <TabsContent value="upload" className="flex-1 overflow-auto p-4 mt-0">
              <UploadDropzone
                submissionId={submission.id}
                onUploadComplete={refetchDocuments}
              />
            </TabsContent>
          </Tabs>
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Center Panel - Document Viewer */}
        <ResizablePanel defaultSize={45}>
          <DocumentViewer document={selectedDocument} />
        </ResizablePanel>

        <ResizableHandle withHandle />

        {/* Right Panel - Risk Profile */}
        <ResizablePanel defaultSize={30} minSize={25} maxSize={40}>
          <RiskProfilePanel submissionId={submission.id} />
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}
